import { React, useState, useEffect, useRef, useLayoutEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import ProductCard from "../../components/product/ProductCard";
import ProductSpecialCard from "../../components/product/ProductSpecialCard";
import { getProducts } from "../../constants/products";
import { main_paige_title } from "../../constants/constants";

const Shop = () => {
  const targetRef = useRef();
  const [isMobile, setIsMobile] = useState(false);
  const [products, setProducts] = useState([]);
  const [sortOption, setSortOption] = useState("standard");
  // We'll compute the number of columns for the grid based on window width.
  const [columns, setColumns] = useState(4);

  // Detect mobile size for other purposes.
  useLayoutEffect(() => {
    if (targetRef.current) {
      setIsMobile(window.innerWidth < 768);
    }
  }, []);

  // Set products from API.
  useEffect(() => {
    setProducts(getProducts());
  }, []);

  // Determine grid column count based on window width.
  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width < 576) setColumns(1);
      else if (width < 992) setColumns(2);
      else setColumns(4);
    };
    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  const handleSortChange = (option, event) => {
    event.preventDefault();
    setSortOption(option);
  };

  // Compute sorted products.
  const sortedProducts = useMemo(() => {
    let sorted = [...products];
    if (sortOption === "asc") {
      sorted.sort((a, b) => a.product_price - b.product_price);
    } else if (sortOption === "desc") {
      sorted.sort((a, b) => b.product_price - a.product_price);
    }
    // "standard" leaves the order unchanged.
    return sorted;
  }, [products, sortOption]);

  // Compute effective grid span for each product.
  // If a product with grid_type == 2 cannot span 2 columns because there is only one column left,
  // we set a fallback flag to render it as a ProductCard.
  const productsWithSpan = useMemo(() => {
    let rowColumnsUsed = 0;
    return sortedProducts.map((element) => {
      let effectiveSpan = 1;
      let fallback = false;
      if (element.grid_type == 2) { // using loose comparison in case it's a number
        if (rowColumnsUsed + 2 <= columns) {
          effectiveSpan = 2;
          rowColumnsUsed += 2;
        } else {
          // Not enough space left; fall back to a single-column item (ProductCard)
          effectiveSpan = 1;
          fallback = true;
          rowColumnsUsed += 1;
        }
      } else {
        effectiveSpan = 1;
        rowColumnsUsed += 1;
      }
      if (rowColumnsUsed >= columns) {
        rowColumnsUsed = 0;
      }
      return { ...element, effectiveSpan, fallback };
    });
  }, [sortedProducts, columns]);

  const sortDisplayText =
    sortOption === "asc"
      ? "Preis niedrig zu hoch"
      : sortOption === "desc"
        ? "Preis hoch zu niedrig"
        : "Standard";

  return (
    <div>
      <Helmet>
        <title property="title">{main_paige_title} - Shop</title>
      </Helmet>
      <Header />
      <main className="py-8 page-title border-top" id="content" ref={targetRef}>
        {/* Page Title Section */}
        <section className="py-8 page-title border-top" style={{ marginTop: "10px" }}>
          <div className="container">
            <h1 className="fs-40 mb-1 text-capitalize text-center">Shop</h1>
          </div>
        </section>
        {/* Main Content Section */}
        <section>
          <div className="container container-xxl">
            {/* Filter & Sorting */}
            <div className="d-flex mb-7">
              <div className="ml-auto">
                <div className="dropdown">
                  <a
                    href="#"
                    className="dropdown-toggle fs-14"
                    id="dropdownMenuButton"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                  >
                    {sortDisplayText}
                  </a>
                  <div className="dropdown-menu dropdown-menu-right" aria-labelledby="dropdownMenuButton">
                    <a
                      className="dropdown-item text-primary fs-14"
                      href="#"
                      onClick={(e) => handleSortChange("asc", e)}
                    >
                      Preis niedrig zu hoch
                    </a>
                    <a
                      className="dropdown-item text-primary fs-14"
                      href="#"
                      onClick={(e) => handleSortChange("desc", e)}
                    >
                      Preis hoch zu niedrig
                    </a>
                    <a
                      className="dropdown-item text-primary fs-14"
                      href="#"
                      onClick={(e) => handleSortChange("standard", e)}
                    >
                      Standard
                    </a>
                  </div>
                </div>
              </div>
            </div>
            {/* Products Grid */}
            <div
              className="d-grid g-1 g-sm-2 g-lg-4 grid-gap overflow-hidden mb-10"
              style={{ gridAutoFlow: "row dense" }}
            >
              {productsWithSpan.map((element, index) => {
                const prevProduct = index > 0 ? productsWithSpan[index - 1] : null;
                const nextProduct = index < productsWithSpan.length - 1 ? productsWithSpan[index + 1] : null;
                return (
                  <div
                    key={element.id}
                    className="grid-item"
                    style={{ gridColumn: `span ${element.effectiveSpan}` }}
                  >
                    {element.grid_type == 2 && !element.fallback ? (
                      <ProductSpecialCard
                        element={element}
                        isMobile={isMobile}
                        prevProduct={prevProduct}
                        nextProduct={nextProduct}
                        allProducts={productsWithSpan}
                      />
                    ) : (
                      <ProductCard
                        element={element}
                        isMobile={isMobile}
                        prevProduct={prevProduct}
                        nextProduct={nextProduct}
                        allProducts={productsWithSpan}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Shop;